import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from '@web-stories-wp/react';
import { useAPI } from '../api';
import { useStory } from '../story';
import Context from './context';

const LOAD_ERROR = 'Unable to load LUT filters.';
const RESOLUTION_ERROR = 'Unable to resolve the saved LUT filter.';

const normalizeVersionId = (versionId) =>
  typeof versionId === 'string' ? versionId.trim().toLowerCase() : '';

export function getReferencedLutVersionIds(pages) {
  const versionIds = new Set();

  for (const page of Array.isArray(pages) ? pages : []) {
    for (const element of Array.isArray(page?.elements) ? page.elements : []) {
      if (element?.type !== 'image') {
        continue;
      }

      const versionId = normalizeVersionId(element.lutFilter?.versionId);
      if (versionId) {
        versionIds.add(versionId);
      }
    }
  }

  return [...versionIds].sort();
}

const getAssetMap = (assets) =>
  Object.fromEntries(
    (Array.isArray(assets) ? assets : [])
      .map((asset) => [normalizeVersionId(asset?.versionId), asset])
      .filter(([versionId]) => versionId)
  );

const getCatalogueAssets = (filters) =>
  (Array.isArray(filters) ? filters : [])
    .map((filter) => filter?.currentVersion)
    .filter(Boolean)
    .map((version) => ({
      ...version,
      versionId: version.id,
    }));

function LutFilterProvider({ children }) {
  const {
    actions: { getStudioLutFilters, resolveStudioLutVersions },
  } = useAPI();
  const referencedVersionIdsKey = useStory(({ state: { pages } }) =>
    getReferencedLutVersionIds(pages).join(',')
  );
  const [state, setState] = useState({
    assetsByVersionId: {},
    filters: [],
    isLoading: Boolean(getStudioLutFilters),
    loadError: null,
    resolutionError: null,
  });

  useEffect(() => {
    if (!getStudioLutFilters) {
      setState({
        assetsByVersionId: {},
        filters: [],
        isLoading: false,
        loadError: null,
        resolutionError: null,
      });
      return undefined;
    }

    const abortController = new AbortController();
    setState({
      assetsByVersionId: {},
      filters: [],
      isLoading: true,
      loadError: null,
      resolutionError: null,
    });

    getStudioLutFilters({ signal: abortController.signal })
      .then(({ filters = [] }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setState((currentState) => ({
          ...currentState,
          assetsByVersionId: {
            ...currentState.assetsByVersionId,
            ...getAssetMap(getCatalogueAssets(filters)),
          },
          filters,
          isLoading: false,
          loadError: null,
        }));
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setState((currentState) => ({
            ...currentState,
            filters: [],
            isLoading: false,
            loadError: LOAD_ERROR,
          }));
        }
      });

    return () => abortController.abort();
  }, [getStudioLutFilters]);

  useEffect(() => {
    if (!referencedVersionIdsKey || !resolveStudioLutVersions) {
      return undefined;
    }

    const abortController = new AbortController();
    const versionIds = referencedVersionIdsKey.split(',');
    setState((currentState) => ({
      ...currentState,
      resolutionError: null,
    }));

    resolveStudioLutVersions(versionIds, {
      signal: abortController.signal,
    })
      .then(({ assets = [] }) => {
        if (abortController.signal.aborted) {
          return;
        }

        setState((currentState) => ({
          ...currentState,
          assetsByVersionId: {
            ...currentState.assetsByVersionId,
            ...getAssetMap(assets),
          },
          resolutionError: null,
        }));
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setState((currentState) => ({
            ...currentState,
            resolutionError: RESOLUTION_ERROR,
          }));
        }
      });

    return () => abortController.abort();
  }, [referencedVersionIdsKey, resolveStudioLutVersions]);

  const contextValue = useMemo(() => ({ state }), [state]);

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

LutFilterProvider.propTypes = {
  children: PropTypes.node,
};

export default LutFilterProvider;
